import { Feature, Polygon } from '@turf/turf';
import { WeatherServiceParams } from '../../types/weather';

const XWEATHER_CLIENT_ID = 'xJsdJI7QdOGbChV6d7qb8';
const XWEATHER_CLIENT_SECRET = '773jSSPfQN8Mr2aGWQ3Mpqbz3kk6bpLUTlqIcljP';

export async function fetchXWeatherData(polygons: Feature<Polygon>[], params: WeatherServiceParams) {
  try {
    // Filter out invalid polygons first
    const validPolygons = polygons.filter(poly => {
      if (!poly?.geometry?.coordinates?.[0]?.length) {
        console.warn('Skipping empty polygon');
        return false;
      }
      if (poly.geometry.coordinates[0].length < 3) {
        console.warn('Skipping polygon with insufficient points');
        return false;
      }
      return true;
    });

    if (validPolygons.length === 0) {
      throw new Error('No valid polygons found. Please draw a complete polygon on the map.');
    }

    const locationColors = [
      '#00ff00', '#0000ff', '#ffa500', 
      '#800080', '#00ffff', '#ff00ff', 
      '#aaaa00', '#00aaaa'
    ];

    // Process each valid polygon
    const allReports = await Promise.all(
      validPolygons.map(async (poly, index) => {
        // Convert coordinates to API format and ensure it's closed
        const coords = poly.geometry.coordinates[0];
        const coordinates = coords.map(coord => ({
          lat: coord[1],
          lon: coord[0]
        }));

        // Ensure polygon is closed by adding first point at end if needed
        if (coords[0][0] !== coords[coords.length - 1][0] || 
            coords[0][1] !== coords[coords.length - 1][1]) {
          coordinates.push({
            lat: coords[0][1],
            lon: coords[0][0]
          });
        }

        // Build request body
        const requestBody: any = {
          action: 'within',
          parameter: params.parameter === 'tropicalcyclones' ? 'tropicalcyclones/archive' : 'stormreports',
          polygon_coords: coordinates,
          start_date: params.startDate,
          end_date: params.endDate,
          format: 'json',
          limit: 100000
        };

        // Add storm filter for storm reports
        if (params.parameter === 'storm_reports' && params.stormFilter && params.stormFilter !== 'all') {
          requestBody.filter = params.stormFilter;

          // Add measurement-specific queries
          switch (params.stormFilter) {
            case 'hail':
              if (params.hailThreshold) {
                requestBody.query = `detail.hailIN:>${params.hailThreshold}`;
              }
              break;
            case 'wind':
              if (params.triggerValue) {
                requestBody.query = `detail.windSpeedMPH:${params.triggerOperator}${params.triggerValue}`;
              }
              break;
            case 'snow':
              if (params.triggerValue) {
                requestBody.query = `detail.snowIN:${params.triggerOperator}${params.triggerValue}`;
              }
              break;
            case 'rain':
              if (params.triggerValue) {
                requestBody.query = `detail.rainIN:${params.triggerOperator}${params.triggerValue}`;
              }
              break;
            case 'ice':
              if (params.triggerValue) {
                requestBody.query = `detail.iceIN:${params.triggerOperator}${params.triggerValue}`;
              }
              break;
          }
        }

        console.log('Sending request for polygon', index + 1, 'with body:', requestBody);

        const response = await fetch('https://api-dev.catxre.com/weather/weather-data-x', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Weather-ID': XWEATHER_CLIENT_ID,
            'X-Weather-Secret': XWEATHER_CLIENT_SECRET
          },
          body: JSON.stringify(requestBody)
        });

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`API error (${response.status}): ${errorText}`);
        }

        const data = await response.json();
        
        if (!data.response || !Array.isArray(data.response)) {
          console.error('Invalid API response:', data);
          return [];
        }

        // Process based on data type
        if (params.parameter === 'tropicalcyclones') {
          return data.response.map((cyclone: any) => ({
            id: cyclone.id,
            profile: cyclone.profile,
            track: cyclone.track,
            locationName: `Polygon ${index + 1}`,
            locationColor: locationColors[index % locationColors.length],
            locationIndex: index,
            polygon: poly
          }));
        } else {
          return data.response.map((report: any) => ({
            id: report.id,
            loc: report.loc,
            report: {
              ...report.report,
              dateTimeISO: report.report?.dateTimeISO || report.report?.datetime,
              cat: report.report?.cat?.toLowerCase(),
              type: report.report?.type,
              name: report.report?.name,
              detail: report.report?.detail,
              reporter: report.report?.reporter,
              comments: report.report?.comments
            },
            locationName: `Polygon ${index + 1}`,
            locationColor: locationColors[index % locationColors.length],
            locationIndex: index,
            polygon: poly
          }));
        }
      })
    );

    // Flatten and filter out empty results
    const results = allReports.flat().filter(Boolean);

    if (results.length === 0) {
      throw new Error('No reports found in the selected areas');
    }

    return results;
  } catch (error) {
    console.error('XWeather service error:', error);
    throw error instanceof Error ? error : new Error('Failed to fetch weather data');
  }
}